import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Navigation, 
  Phone, 
  Clock, 
  MapPin, 
  RefreshCw,
  Car,
  Loader2,
  Radio,
  User,
  ArrowRight,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = 'AIzaSyAxrRnVEkT4JfwXhvko7GlMm6hT7LZxlgc';

// Default center (Spain)
const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
const DEFAULT_ZOOM = 6;

const ClientGPSLiveTab = () => {
  const [activeServices, setActiveServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [etaData, setEtaData] = useState({}); // {service_id: {duration: "45 min", distance: "62 km"}}
  
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const distanceServiceRef = useRef(null);

  // Initialize map when container is ready
  const initializeMap = useCallback((container) => {
    if (!container || mapInstanceRef.current) return;
    
    // Check if Google Maps is available
    if (!window.google || !window.google.maps) {
      console.log('⏳ Waiting for Google Maps API...');
      return;
    }

    console.log('🗺️ Initializing Google Map...');
    try {
      mapInstanceRef.current = new window.google.maps.Map(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,  // Enable zoom controls (+/-)
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER
        },
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });
      
      infoWindowRef.current = new window.google.maps.InfoWindow();
      
      // Initialize Distance Matrix Service for ETA calculations
      distanceServiceRef.current = new window.google.maps.DistanceMatrixService();
      
      setMapInitialized(true);
      console.log('✅ Google Map initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing map:', error);
    }
  }, []);

  // Callback ref for map container
  const mapRef = useCallback((node) => {
    mapContainerRef.current = node;
    if (node && mapLoaded && !mapInstanceRef.current) {
      initializeMap(node);
    }
  }, [mapLoaded, initializeMap]);

  // Load/check Google Maps Script
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        console.log('✅ Google Maps already loaded');
        setMapLoaded(true);
        // Try to initialize if container is already mounted
        if (mapContainerRef.current && !mapInstanceRef.current) {
          initializeMap(mapContainerRef.current);
        }
        return true;
      }
      return false;
    };

    // If already loaded, set state immediately
    if (checkGoogleMaps()) return;

    // Otherwise, wait for it to load (App.js should load it)
    const intervalId = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(intervalId);
      }
    }, 100);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [initializeMap]);

  // Calculate ETA for all active services
  const calculateETAs = useCallback(async () => {
    if (!distanceServiceRef.current || activeServices.length === 0) return;
    
    const servicesWithLocation = activeServices.filter(
      s => s.current_location?.lat && s.current_location?.lng && 
           s.delivery_location?.lat && s.delivery_location?.lng
    );
    
    if (servicesWithLocation.length === 0) return;
    
    // Process in batches (Distance Matrix API has limits)
    for (const service of servicesWithLocation) {
      try {
        const origin = new window.google.maps.LatLng(
          service.current_location.lat,
          service.current_location.lng
        );
        const destination = new window.google.maps.LatLng(
          service.delivery_location.lat,
          service.delivery_location.lng
        );
        
        distanceServiceRef.current.getDistanceMatrix(
          {
            origins: [origin],
            destinations: [destination],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
          },
          (response, status) => {
            if (status === 'OK' && response.rows[0]?.elements[0]?.status === 'OK') {
              const element = response.rows[0].elements[0];
              setEtaData(prev => ({
                ...prev,
                [service.id]: {
                  duration: element.duration.text,
                  durationValue: element.duration.value, // seconds
                  distance: element.distance.text,
                  distanceValue: element.distance.value, // meters
                  calculatedAt: new Date()
                }
              }));
            }
          }
        );
      } catch (error) {
        console.error('Error calculating ETA for service:', service.id, error);
      }
    }
  }, [activeServices]);

  // Calculate ETAs when services change
  useEffect(() => {
    if (mapInitialized && activeServices.length > 0) {
      calculateETAs();
    }
  }, [mapInitialized, activeServices, calculateETAs]);

  // Update markers when services change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    let hasValidLocations = false;

    activeServices.forEach(service => {
      if (!service.current_location?.lat || !service.current_location?.lng) return;

      hasValidLocations = true;
      const position = {
        lat: service.current_location.lat,
        lng: service.current_location.lng
      };

      bounds.extend(position);

      const plate = service.vehicle_info?.plate || '???';
      
      // Create marker with AutoGo car icon and license plate
      const marker = new window.google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: `${plate} - ${service.vehicle_info?.brand || ''} ${service.vehicle_info?.model || ''}`,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="70" height="95" viewBox="0 0 70 95">
              <!-- License plate background -->
              <rect x="7" y="0" width="56" height="22" rx="5" fill="#0f2942" stroke="#4ade80" stroke-width="2"/>
              <!-- License plate text -->
              <text x="35" y="16" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="12" font-weight="bold">${plate}</text>
              <!-- Connector line -->
              <line x1="35" y1="22" x2="35" y2="28" stroke="#4ade80" stroke-width="2"/>
              <!-- Main icon background (rounded square) -->
              <rect x="5" y="28" width="60" height="60" rx="12" fill="#0f2942"/>
              <!-- Car icon (front view) - green car -->
              <g transform="translate(35, 58)">
                <!-- Car body -->
                <path d="M -20,15 L -22,5 L -20,-5 L -15,-15 L -5,-20 L 5,-20 L 15,-15 L 20,-5 L 22,5 L 20,15 L 15,20 L -15,20 Z" fill="#4ade80"/>
                <!-- Windshield -->
                <path d="M -12,-12 L -8,-18 L 8,-18 L 12,-12 L 10,-5 L -10,-5 Z" fill="#0f2942"/>
                <!-- Left headlight -->
                <circle cx="-14" cy="0" r="5" fill="#0f2942"/>
                <!-- Right headlight -->
                <circle cx="14" cy="0" r="5" fill="#0f2942"/>
                <!-- Grille -->
                <rect x="-10" y="8" width="20" height="8" rx="2" fill="#0f2942"/>
                <!-- Left mirror -->
                <ellipse cx="-20" cy="-10" rx="3" ry="5" fill="#4ade80"/>
                <!-- Right mirror -->
                <ellipse cx="20" cy="-10" rx="3" ry="5" fill="#4ade80"/>
                <!-- Left fog light -->
                <circle cx="-12" cy="12" r="2" fill="#0f2942"/>
                <!-- Right fog light -->
                <circle cx="12" cy="12" r="2" fill="#0f2942"/>
              </g>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(70, 95),
          anchor: new window.google.maps.Point(35, 90)
        },
        zIndex: 10
      });

      // Click handler to show address and ETA
      marker.addListener('click', async () => {
        setSelectedService(service);
        
        // Get ETA data for this service
        const serviceEta = etaData[service.id];
        
        // Reverse geocode to get address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: position }, (results, status) => {
          if (status === 'OK' && results[0]) {
            setSelectedAddress(results[0].formatted_address);
            
            const infoContent = `
              <div style="padding: 10px; max-width: 300px; font-family: system-ui, -apple-system, sans-serif;">
                <div style="font-weight: bold; font-size: 16px; color: #1e40af; margin-bottom: 8px;">
                  🚗 ${service.vehicle_info?.plate || 'N/A'}
                </div>
                <div style="color: #374151; font-size: 13px; margin-bottom: 4px;">
                  <strong>${service.vehicle_info?.brand || ''} ${service.vehicle_info?.model || ''}</strong>
                </div>
                <div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
                  📍 <strong>Ubicación actual:</strong><br/>
                  ${results[0].formatted_address}
                </div>
                ${service.delivery_location?.address ? `
                  <div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
                    🏁 <strong>Destino:</strong><br/>
                    ${service.delivery_location.address}
                  </div>
                ` : ''}
                ${serviceEta ? `
                  <div style="background: #ecfdf5; border: 1px solid #10b981; border-radius: 8px; padding: 10px; margin: 10px 0;">
                    <div style="color: #059669; font-size: 12px; font-weight: 600;">
                      ⏱️ ETA para destino
                    </div>
                    <div style="color: #047857; font-size: 22px; font-weight: bold; margin-top: 4px;">
                      ${serviceEta.duration}
                    </div>
                    <div style="color: #6b7280; font-size: 11px; margin-top: 2px;">
                      ${serviceEta.distance} restantes
                    </div>
                  </div>
                ` : `
                  <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 8px; margin: 10px 0;">
                    <div style="color: #b45309; font-size: 12px;">
                      ⏳ Calculando ETA...
                    </div>
                  </div>
                `}
                ${service.driver_info ? `
                  <div style="color: #374151; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 8px;">
                    <strong>Conductor:</strong> ${service.driver_info.name || 'N/A'}<br/>
                    ${service.driver_info.phone ? `<a href="tel:${service.driver_info.phone}" style="color: #2563eb; text-decoration: none;">📞 ${service.driver_info.phone}</a>` : ''}
                  </div>
                ` : ''}
              </div>
            `;
            
            infoWindowRef.current.setContent(infoContent);
            infoWindowRef.current.open(mapInstanceRef.current, marker);
          } else {
            setSelectedAddress('Dirección no disponible');
          }
        });
      });

      markersRef.current.push(marker);
    });

    // Fit bounds if we have valid locations, but keep reasonable zoom
    if (hasValidLocations && markersRef.current.length > 0) {
      // For single vehicle or few vehicles, don't auto-zoom too much
      // Let user zoom in if they want - default to Spain view
      const currentZoom = mapInstanceRef.current.getZoom();
      if (currentZoom < 7) {
        // If zoomed out too much, fit to bounds but limit zoom
        mapInstanceRef.current.fitBounds(bounds);
        setTimeout(() => {
          if (mapInstanceRef.current.getZoom() > 8) {
            mapInstanceRef.current.setZoom(8);
          }
        }, 100);
      }
      // Otherwise, just pan to center of bounds without changing zoom
      else {
        const center = bounds.getCenter();
        mapInstanceRef.current.panTo(center);
      }
    }
  }, [activeServices, mapInitialized, etaData]);

  const fetchActiveServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token') || localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/clients/transport-services/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveServices(data.services || []);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching active services:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveServices();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchActiveServices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchActiveServices]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchActiveServices();
  };

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando vehículos en ruta...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-gps-live-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-blue-600" />
            GPS en Vivo
          </h2>
          <p className="text-gray-600 mt-1">
            Monitorea tus vehículos en tránsito en tiempo real
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {activeServices.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
              <Radio className="w-4 h-4 text-green-600 animate-pulse" />
              <span className="text-sm font-medium text-green-700">
                {activeServices.length} EN RUTA
              </span>
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-gps-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Last Update Info */}
      {lastUpdate && (
        <p className="text-sm text-gray-500">
          Última actualización: {lastUpdate.toLocaleTimeString('es-ES')} 
          <span className="text-gray-400 ml-2">(Auto-refresh cada 30s)</span>
        </p>
      )}

      {/* Google Maps - Always visible */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Mapa de Vehículos 
            {activeServices.length > 0 && (
              <Badge className="ml-2 bg-blue-100 text-blue-700">
                {activeServices.length} vehículo{activeServices.length !== 1 ? 's' : ''} en ruta
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Map Container - Always shown */}
          <div className="relative">
            <div 
              ref={mapRef}
              className="w-full h-[450px] rounded-lg border bg-gray-100"
              data-testid="gps-map-container"
              style={{ minHeight: '450px' }}
            />
            {!mapInitialized && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Cargando mapa...</span>
              </div>
            )}
          </div>
          
          {/* Empty state message */}
          {activeServices.length === 0 && mapInitialized && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
              <Car className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">No hay vehículos en ruta actualmente</p>
              <p className="text-gray-400 text-sm mt-1">
                Cuando un conductor inicie un transporte, aparecerá en el mapa
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
              <span>Vehículo en ruta</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">|</span>
              <span>Haz clic en un vehículo para ver su ubicación exacta</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Cards - Horizontal Scroll */}
      {activeServices.length > 0 && (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
          {activeServices.map(service => (
            <Card 
              key={service.id} 
              className={`cursor-pointer transition-all hover:shadow-lg flex-shrink-0 w-[340px] ${selectedService?.id === service.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
              onClick={() => {
                setSelectedService(service);
                // Center map on this vehicle
                if (mapInstanceRef.current && service.current_location?.lat) {
                  mapInstanceRef.current.panTo({
                    lat: service.current_location.lat,
                    lng: service.current_location.lng
                  });
                  mapInstanceRef.current.setZoom(14);
                }
              }}
              data-testid={`vehicle-card-${service.vehicle_info?.plate}`}
            >
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Left: Vehicle Icon */}
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Car className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  {/* Right: Info */}
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-lg text-gray-900">{service.vehicle_info?.plate || 'N/A'}</span>
                      <Badge className="bg-green-100 text-green-700 text-xs">En Ruta</Badge>
                    </div>
                    
                    {/* Vehicle Model */}
                    <p className="text-gray-500 text-sm mb-2">
                      {service.vehicle_info?.brand} {service.vehicle_info?.model}
                    </p>
                    
                    {/* Route */}
                    <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                      <span className="truncate">{service.pickup_location?.address?.split(',')[0] || 'Origen'}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0 mx-1" />
                      <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
                      <span className="truncate">{service.delivery_location?.address?.split(',')[0] || 'Destino'}</span>
                    </div>
                    
                    {/* Driver with phone inline */}
                    {service.driver_info && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <User className="w-3 h-3 text-gray-400" />
                        <span className="font-medium">{service.driver_info.name}</span>
                        {service.driver_info.phone && (
                          <>
                            <span className="text-gray-400">|</span>
                            <a 
                              href={`tel:${service.driver_info.phone}`}
                              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone className="w-3 h-3" />
                              <span>{service.driver_info.phone}</span>
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* ETA Bar - Full Width */}
                {etaData[service.id] ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                    <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-green-800 text-xs font-medium">ETA para destino:</span>
                      <span className="text-green-700 font-bold ml-2">{etaData[service.id].duration}</span>
                      <span className="text-gray-500 text-xs ml-2">({etaData[service.id].distance})</span>
                    </div>
                  </div>
                ) : service.delivery_location?.lat && service.delivery_location?.lng ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-gray-50 rounded-lg text-sm text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Calculando ETA...</span>
                  </div>
                ) : null}

                {/* Last Update */}
                {service.current_location?.updated_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    Última posición: {formatTime(service.current_location.updated_at)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      )}

      {/* Selected Vehicle Detail Panel */}
      {selectedService && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                {selectedService.vehicle_info?.plate} - Detalle
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => {
                setSelectedService(null);
                setSelectedAddress(null);
                if (infoWindowRef.current) {
                  infoWindowRef.current.close();
                }
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Vehículo</p>
                <p className="font-medium">{selectedService.vehicle_info?.brand} {selectedService.vehicle_info?.model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Conductor</p>
                <p className="font-medium">{selectedService.driver_info?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Origen</p>
                <p className="font-medium text-sm">{selectedService.pickup_location?.address || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Destino</p>
                <p className="font-medium text-sm">{selectedService.delivery_location?.address || 'N/A'}</p>
              </div>
              {selectedAddress && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">Ubicación Actual</p>
                  <p className="font-medium text-blue-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedAddress}
                  </p>
                </div>
              )}
              {/* ETA in detail panel */}
              {etaData[selectedService.id] && (
                <div className="col-span-2 bg-green-100 rounded-lg p-3 border border-green-300">
                  <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    ETA para destino
                  </p>
                  <p className="text-2xl font-bold text-green-800 mt-1">
                    {etaData[selectedService.id].duration}
                  </p>
                  <p className="text-sm text-green-600">
                    {etaData[selectedService.id].distance} restantes
                  </p>
                </div>
              )}
              {selectedService.driver_info?.phone && (
                <div className="col-span-2">
                  <a 
                    href={`tel:${selectedService.driver_info.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Phone className="w-4 h-4" />
                    Llamar al conductor
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientGPSLiveTab;
